'use client';

import { PortableText } from '@portabletext/react';
import { GeneralDescriptionSection } from '@/types/sanity';
import { Card, CardContent } from '@/components/ui/card';
import { SectionCTAs } from './SectionCTAs';
import Image from 'next/image';
import { urlFor } from '@/sanity/lib/image';
import { ScrollReveal } from '@/components/animations/ScrollAnimations';

import { cn } from '@/lib/utils';
import { ExpandableText } from '@/components/ui/expandable-text';

interface GeneralDescriptionSectionComponentProps {
  section: GeneralDescriptionSection;
  pageSlug?: string;
}

const LEGAL_PAGE_SLUGS = new Set(['privacy', 'privacy-policy', 'terms', 'terms-of-service']);

export function GeneralDescriptionSectionComponent({
  section,
  pageSlug,
}: GeneralDescriptionSectionComponentProps) {
  const hasImage = section.image?.asset?._ref;
  const imagePosition = section.imagePosition || 'right';
  const isHorizontal = imagePosition === 'left' || imagePosition === 'right';
  const isLegalPage = pageSlug ? LEGAL_PAGE_SLUGS.has(pageSlug) : false;

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'general-description-section py-8 md:py-12 lg:py-16',
          pageSlug && `general-description-section--${pageSlug}`
        )}
        style={
          section.backgroundImage?.asset?._ref
            ? {
                backgroundImage: `url(${urlFor(section.backgroundImage).width(1920).quality(80).url()})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto px-6 sm:px-0">
            <div className="mb-12 text-center">
              <h2 className="font-medium mb-4 text-section-title leading-section-title">
                {section.title}
              </h2>
              {section.subtitle && (
                <p className="text-muted-foreground text-section-subtitle leading-section-subtitle">
                  {section.subtitle}
                </p>
              )}
            </div>

            {hasImage && isHorizontal ? (
              <div
                className={`grid md:grid-cols-2 gap-8 items-center ${imagePosition === 'left' ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={imagePosition === 'left' ? 'md:order-2' : ''}>
                  {section.description &&
                    (isLegalPage ? (
                      <div className="prose prose-lg max-w-none">
                        <PortableText value={section.description} />
                      </div>
                    ) : (
                      <ExpandableText
                        blocks={section.description}
                        className="prose prose-lg max-w-none"
                      >
                        <PortableText value={section.description} />
                      </ExpandableText>
                    ))}
                </div>
                <div className={imagePosition === 'left' ? 'md:order-1' : ''}>
                  <Card>
                    <CardContent className="p-0">
                      {section.image && (
                        <Image
                          src={urlFor(section.image).width(800).quality(80).url()}
                          alt={section.image?.alt || section.title}
                          width={600}
                          height={400}
                          className="w-full h-auto rounded-lg"
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : hasImage ? (
              <div className="space-y-8">
                {imagePosition === 'top' && section.image && (
                  <Card className="mx-auto max-w-3xl">
                    <CardContent className="p-0">
                      <Image
                        src={urlFor(section.image).width(800).quality(80).url()}
                        alt={section.image?.alt || section.title}
                        width={800}
                        height={400}
                        className="w-full h-auto rounded-lg"
                      />
                    </CardContent>
                  </Card>
                )}
                {section.description &&
                  (isLegalPage ? (
                    <div className="prose prose-lg max-w-none mx-auto">
                      <PortableText value={section.description} />
                    </div>
                  ) : (
                    <ExpandableText
                      blocks={section.description}
                      className="prose prose-lg max-w-none mx-auto"
                    >
                      <PortableText value={section.description} />
                    </ExpandableText>
                  ))}
                {imagePosition === 'bottom' && section.image && (
                  <Card className="mx-auto max-w-3xl">
                    <CardContent className="p-0">
                      <Image
                        src={urlFor(section.image).width(800).quality(80).url()}
                        alt={section.image?.alt || section.title}
                        width={800}
                        height={400}
                        className="w-full h-auto rounded-lg"
                      />
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              section.description &&
              (isLegalPage ? (
                <div className="prose prose-lg max-w-4xl mx-auto">
                  <PortableText value={section.description} />
                </div>
              ) : (
                <ExpandableText
                  blocks={section.description}
                  className="prose prose-lg max-w-4xl mx-auto"
                >
                  <PortableText value={section.description} />
                </ExpandableText>
              ))
            )}

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
